
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function QuoteForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [showResults, setShowResults] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    dob: '',
    height: '',
    weight: '',
    gender: '',
    zip: '',
    state: '',
    householdSize: '',
    income: '',
    conditions: ''
  });

  const states = [
    'FL', 'AL', 'CO', 'DE', 'GA', 'IA', 'IL', 'IN', 'KS', 'KY', 
    'LA', 'MD', 'MI', 'MO', 'MS', 'MT', 'NC', 'NE', 'NV', 'OH', 
    'OK', 'SC', 'SD', 'TN', 'TX', 'UT', 'VA', 'WI', 'WV', 'WY'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3jaf65soafcrsrcqb1g', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setShowResults(true);
      } else {
        alert('There was an error submitting your form. Please try again.');
      }
    } catch (error) {
      alert('There was an error submitting your form. Please try again.');
    }
  };

  const nextStep = () => {
    if (currentStep < 3) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  if (showResults) {
    return (
      <section id="quote" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-green-50 border border-green-200 rounded-2xl p-8">
            <div className="w-16 h-16 bg-green-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-white text-2xl"></i>
            </div>
            
            <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Thank You for Your Request!
            </h2>
            
            <p className="text-lg text-gray-700 mb-8">
              Your information has been received. Choose how you'd like to proceed:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Book Consultation */}
              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
                <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-calendar-line text-black text-xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Book a Consultation</h3>
                <p className="text-gray-600 mb-4">Schedule a 30-minute call to discuss your options</p>
                <Button 
                  onClick={() => window.open('https://calendly.com/nicholasdearaujo1/30min', '_blank')}
                  variant="secondary"
                  className="w-full"
                >
                  Schedule Now
                </Button>
              </div>

              {/* Request Call/Text */}
              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
                <div className="w-12 h-12 bg-green-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-phone-line text-white text-xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Request a Call/Text</h3>
                <p className="text-gray-600 mb-4">Get in touch directly for immediate assistance</p>
                <div className="space-y-2">
                  <a 
                    href="tel:5612295893"
                    className="block w-full bg-green-800 hover:bg-green-700 text-white py-2 px-4 rounded-lg transition-colors text-center"
                  >
                    Call (561) 229-5893
                  </a>
                  <a 
                    href="sms:5612295893"
                    className="block w-full bg-gray-800 hover:bg-gray-7
                    text-white py-2 px-4 rounded-lg transition-colors text-center"
                  >
                    Send Text Message
                  </a>
                </div>
              </div>
            </div>

            {/* Calendly Embed */}
            <div className="mt-12">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Or Schedule Directly Below:
              </h3>
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div 
                  className="calendly-inline-widget" 
                  data-url="https://calendly.com/nicholasdearaujo1/30min" 
                  style={{ minWidth: '320px', height: '700px' }}
                ></div>
              </div>
            </div>

          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="quote" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Get Your <span className="text-green-800">Free Quote</span>
          </h2>
          <p className="text-xl text-gray-600">
            Tell us about yourself and we'll find the perfect health plan for you
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4 mb-4">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                  currentStep >= step 
                    ? 'bg-green-800 text-white' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {step}
                </div>
                {step < 3 && (
                  <div className={`w-16 h-1 mx-2 ${
                    currentStep > step ? 'bg-green-800' : 'bg-gray-200'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
          <div className="text-center text-sm text-gray-600">
            Step {currentStep} of 3
          </div>
        </div>

        <form onSubmit={handleSubmit} data-readdy-form className="bg-white rounded-2xl shadow-xl p-8">
          {/* Step 1: Personal Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Personal Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-707 mb-2">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="your@email.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-707 mb-2">Date of Birth *</label>
                  <input
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Health & Location */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Health &amp; Location Details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-707 mb-2">Height</label>
                  <input
                    type="text"
                    name="height"
                    value={formData.height}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="5'8&quot;"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-707 mb-2">Weight</label>
                  <input
                    type="text"
                    name="weight"
                    value={formData.weight}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="150 lbs"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-707 mb-2">Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-707 mb-2">ZIP Code *</label>
                  <input
                    type="text"
                    name="zip"
                    value={formData.zip}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="12345"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-707 mb-2">State *</label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select State</option>
                    {states.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Household & Health Conditions */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Household &amp; Health Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-707 mb-2">Household Size *</label>
                  <select
                    name="householdSize"
                    value={formData.householdSize}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select Size</option>
                    <option value="1">1 Person</option>
                    <option value="2">2 People</option>
                    <option value="3">3 People</option>
                    <option value="4">4 People</option>
                    <option value="5+">5+ People</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-707 mb-2">Annual Income *</label>
                  <select
                    name="income"
                    value={formData.income}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select Income Range</option>
                    <option value="<$50,000">Less than $50,000</option>
                    <option value="$50,000-$90,000">$50,000 - $90,000</option>
                    <option value="$90,000+">$90,000+</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-707 mb-2">
                  Health Conditions That Need Coverage
                </label>
                <textarea
                  name="conditions"
                  value={formData.conditions}
                  onChange={handleInputChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm resize-none"
                  placeholder="Please describe any health conditions, medications, or specific coverage needs... (Max 500 characters)"
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.conditions.length}/500 characters
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
            <div>
              {currentStep > 1 && (
                <Button 
                  type="button"
                  onClick={prevStep}
                  variant="outline"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Previous
                </Button>
              )}
            </div>
            
            <div>
              {currentStep < 3 ? (
                <Button 
                  type="button"
                  onClick={nextStep}
                >
                  Next
                  <i className="ri-arrow-right-line ml-2"></i>
                </Button>
              ) : (
                <Button 
                  type="submit"
                  variant="secondary"
                  size="lg"
                >
                  <i className="ri-send-plane-line mr-2"></i>
                  Get My Quote
                </Button>
              )}
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
